import { InjectionToken, Provider } from '@angular/core';
import { DeepPartial } from './generic-translation.service';

/**
 * Generic configuration interface for components with translations
 */
export interface ComponentConfig<T> {
  translations?: DeepPartial<T> | { [key: string]: string };
}

/**
 * Helper to create injection tokens and providers for component configurations
 * 
 * @example
 * // Create token and provider
 * export const MY_COMPONENT_CONFIG = createComponentConfigToken<MyTranslations>('MY_COMPONENT_CONFIG');
 * 
 * export class MyComponentConfigProvider {
 *   static forRoot(config: ComponentConfig<MyTranslations>): Provider {
 *     return createComponentConfigProvider(MY_COMPONENT_CONFIG, config);
 *   }
 * }
 * 
 * // In app.module.ts
 * providers: [
 *   MyComponentConfigProvider.forRoot({
 *     translations: { ... }
 *   })
 * ]
 */

/**
 * Create an injection token for component configuration
 */
export function createComponentConfigToken<T>(tokenName: string): InjectionToken<ComponentConfig<T>> {
  return new InjectionToken<ComponentConfig<T>>(tokenName);
}

/**
 * Create a provider for component configuration
 */
export function createComponentConfigProvider<T>(
  token: InjectionToken<ComponentConfig<T>>,
  config: ComponentConfig<T>
): Provider {
  return {
    provide: token,
    useValue: config
  };
}

/**
 * Helper class to create configuration provider with forRoot pattern
 * 
 * @example
 * export const MY_CONFIG_TOKEN = createComponentConfigToken<MyTranslations>('MY_CONFIG');
 * export const MyConfigProvider = createConfigProvider(MY_CONFIG_TOKEN);
 * 
 * // Usage in module
 * providers: [MyConfigProvider.forRoot({ translations: {...} })]
 */
export function createConfigProvider<T>(token: InjectionToken<ComponentConfig<T>>) {
  return class {
    static forRoot(config: ComponentConfig<T>): Provider {
      return createComponentConfigProvider(token, config);
    }
  };
}
